import React, { useEffect, useMemo, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV_ITEMS, ASSETS, CONTACT } from "@/data/content";
import { useActiveSection } from "@/hooks/useActiveSection";
import { scrollToId } from "@/lib/scroll";
import { cn } from "@/lib/utils";

const COMPACT_AFTER_PX = 32;
const NAV_IDS = NAV_ITEMS.map((n) => n.id);

const offsetFor = (id) => (id === "overview" ? -120 : -72);
const pad = (n) => String(n).padStart(2, "0");

/** Returns true once the page has scrolled past the compact threshold. */
const useCompactNav = () => {
  const [compact, setCompact] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setCompact(v > COMPACT_AFTER_PX));
  useEffect(() => {
    setCompact(window.scrollY > COMPACT_AFTER_PX);
  }, []);
  return compact;
};

const Brand = ({ compact, onClick }) => (
  <a href="#overview" onClick={onClick} className="flex items-center gap-3" aria-label="HR Anexi — back to top" data-testid="nav-logo-link">
    <img
      src={ASSETS.logo}
      alt="HR Anexi"
      className={cn("w-auto transition-[height] duration-300", compact ? "h-6" : "h-8")}
      width="140"
      height="40"
      decoding="async"
    />
    <span className="hidden xl:block h-5 w-px bg-[color:var(--hairline)]" aria-hidden="true" />
    <span className="hidden xl:block font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-ink)]">Employee Engagement Survey</span>
  </a>
);

const DesktopLinks = ({ active, onNavigate }) => (
  <nav aria-label="Primary" className="hidden lg:block">
    <ul className="flex items-center gap-7">
      {NAV_ITEMS.map((item) => {
        const isActive = active === item.id;
        return (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              onClick={onNavigate(item.id)}
              data-testid={`nav-link-${item.id}`}
              data-active={isActive}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "link-underline text-[0.9rem] font-medium tracking-[-0.005em] transition-colors duration-200",
                isActive ? "text-[color:var(--ink)]" : "text-[color:var(--muted-ink)] hover:text-[color:var(--ink)]"
              )}
            >
              {item.label}
            </a>
          </li>
        );
      })}
    </ul>
  </nav>
);

const NavCTA = ({ compact, onClick }) => (
  <a
    href="#contact"
    onClick={onClick}
    data-testid="nav-cta-button"
    className={cn(
      "btn-arrow hidden sm:inline-flex items-center gap-2 rounded-xl bg-[color:var(--navy)] font-medium text-white transition-colors duration-200 hover:bg-[color:var(--navy-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--blue)] focus-visible:ring-offset-2",
      compact ? "h-10 px-4 text-[0.9rem]" : "h-11 px-5 text-[0.95rem]"
    )}
  >
    Talk to Us
    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
  </a>
);

const MobileMenu = ({ open, onOpenChange, active, onNavigate }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetTrigger asChild>
      <button
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[color:var(--hairline)] bg-white text-[color:var(--navy)] lg:hidden"
        aria-label="Open menu"
        data-testid="nav-mobile-menu-button"
      >
        <Menu className="h-5 w-5" />
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="w-[86vw] max-w-sm border-l border-[color:var(--hairline)] bg-[color:var(--ivory)] p-0">
      <SheetTitle className="sr-only">Navigation</SheetTitle>
      <SheetDescription className="sr-only">Jump to a section of the Employee Engagement Survey page</SheetDescription>
      <div className="flex h-full flex-col">
        <div className="border-b border-[color:var(--hairline)] px-6 py-5">
          <img src={ASSETS.logo} alt="HR Anexi" className="h-7 w-auto" />
        </div>
        <ul className="flex flex-col px-3 py-4" data-testid="nav-mobile-links">
          {NAV_ITEMS.map((item, i) => {
            const isActive = active === item.id;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={onNavigate(item.id)}
                  data-testid={`nav-mobile-link-${item.id}`}
                  className={cn(
                    "flex items-center justify-between rounded-xl px-3 py-3.5 text-[1.05rem] font-medium transition-colors",
                    isActive ? "bg-white text-[color:var(--ink)]" : "text-[color:var(--muted-ink)] hover:bg-white"
                  )}
                >
                  <span className="flex items-center gap-4">
                    <span className="font-mono text-[11px] text-[#7A8794]">{pad(i + 1)}</span>
                    {item.label}
                  </span>
                  {isActive ? <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--gold)]" /> : null}
                </a>
              </li>
            );
          })}
        </ul>
        <div className="mt-auto border-t border-[color:var(--hairline)] px-6 py-6">
          <a
            href="#contact"
            onClick={onNavigate("contact")}
            data-testid="nav-mobile-cta-button"
            className="btn-arrow inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[color:var(--navy)] font-medium text-white"
          >
            Talk to Us <ArrowUpRight className="h-4 w-4" />
          </a>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-[#7A8794]">{CONTACT.email}</p>
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

export const Nav = () => {
  const ids = useMemo(() => NAV_IDS, []);
  const active = useActiveSection(ids);
  const compact = useCompactNav();
  const [open, setOpen] = useState(false);

  const navigate = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id, offsetFor(id));
  };

  return (
    <motion.header
      data-testid="site-nav"
      data-compact={compact}
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300",
        compact ? "nav-glass" : "bg-[color:var(--ivory)]"
      )}
      initial={false}
      animate={{ height: compact ? 60 : 80 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="container-x flex h-full items-center justify-between gap-6">
        <Brand compact={compact} onClick={navigate("overview")} />
        <DesktopLinks active={active} onNavigate={navigate} />
        <div className="flex items-center gap-3">
          <NavCTA compact={compact} onClick={navigate("contact")} />
          <MobileMenu open={open} onOpenChange={setOpen} active={active} onNavigate={navigate} />
        </div>
      </div>
    </motion.header>
  );
};
